import {get} from "../../../utils/dotutils";
import {array_dots, list} from "../../../utils/array";

export const validationTypeSort = ["error","warning","valid"];

export type ValidationFieldType = {
    message: string,
    priority?: number,
    type: string|"warning"|"error"|"valid"
}

export type ValidationType = null|{
    fields?: {
        [key: string]: ValidationFieldType|ValidationFieldType[]
    },
    form?: ValidationFieldType[]
}

export const isValid = (validationMessages: ValidationType) => {
    return !Object.values(validationMessages?.fields || {}).some((value) => {
        if (Array.isArray(value)) {
            return value.some((message) => message.type === "error");
        } else {
            return value.type === "error";
        }
    }) && !validationMessages?.form?.some((value) => {
        if (Array.isArray(value)) {
            return value.some((message) => message.type === "error");
        } else {
            return value.type === "error";
        }
    });
}

type DataType = Record<string | number | symbol, any>;

export const valid = async (
    data: DataType,
    field: string,
    value: any,
    validation: (props: {data: DataType, field: string, value: any}) => boolean|string
) => {
    return validation({data, field, value});
}

export const fieldValidation = async (
    messages: ValidationType,
    data: DataType,
    field: string,
    validation: (props: {data: DataType, field: string, value: any}) => boolean|string,
    message: string|((props: {valid: boolean|string, data: DataType, field: string, value: any}) => string),
    type: string|((props: {valid: boolean|string, data: DataType, field: string, value: any}) => string) = "error",
    priority: number = 0,
): Promise<null|ValidationType> => {
    const value = get(data, field);
    const v = await valid(data, field, value, validation);
    const t = type instanceof Function ? await type({valid: v, data, field, value}) : type;
    const m = message instanceof Function ? await message({valid: v, data, field, value}) : message;
    if (type instanceof Function || !v) {
        if (!messages) {
            messages = {fields: {}};
        }
        if (!messages.fields) {
            messages.fields = {};
        }
        if (messages.fields[field]) {
            messages.fields[field] = list(messages.fields[field], {
                type: t,
                message: m,
                priority,
            });
        } else {
            messages.fields[field] = {
                type: t,
                message: m,
                priority,
            }
        }
    }
    return messages;
}

export const formValidation = async (
    messages: ValidationType,
    data: DataType,
    validation: (props: {data: DataType}) => boolean|string,
    message: string|((props: {valid: boolean|string, data: DataType}) => string),
    type: string|((props: {valid: boolean|string, data: DataType}) => string) = "error",
    priority: number = 0,
): Promise<null|ValidationType> => {
    const v = await validation({data});
    const t = type instanceof Function ? await type({valid: v, data}) : type;
    const m = message instanceof Function ? await message({valid: v, data}) : message;
    if (type instanceof Function || v === false || typeof v === "string") {
        if (!messages) {
            messages = {form: []};
        }
        if (!messages.form) {
            messages.form = [];
        }
        let message = {
            type: t,
            message: m,
            priority,
        };
        messages.form.push(message as ValidationFieldType);
    }
    return messages;
}

export type FieldValidationType = {
    field: RegExp|string|string[],
    validation: (props: {data: DataType, field: string, value: any}) => boolean|string,
    message: string|((props: {valid: boolean|string, data: DataType, field: string, value: any}) => string|any),
    type?: string|((props: {valid: boolean|string, data: DataType, field: string, value: any}) => string),
    priority?: number,
};

export type FormValidationType = {
    validation: (props: {data: DataType}) => boolean|string,
    message: string|((props: {valid: boolean|string, data: DataType}) => string|any),
    type?: string|((props: {valid: boolean|string, data: DataType}) => string),
    priority?: number,
};

export const isFieldValidationType = (rule: any): rule is FieldValidationType => rule && rule.field !== undefined;

export const validations = async (
    messages: ValidationType,
    data: DataType,
    validations: (FieldValidationType|FormValidationType)[]
): Promise<null|ValidationType> => {
    for (const rule of validations) {
        if (isFieldValidationType(rule)) {
            if (rule.field instanceof RegExp) {
                let field = rule.field as RegExp;
                let dotted = array_dots(data);
                //let matched = false;
                for (const fieldName of Object.keys(dotted)) {
                    if (fieldName.match(field)) {
                        //matched = true;
                        messages = await fieldValidation(messages, data, fieldName, rule.validation, rule.message, rule.type, rule.priority);
                    }
                }
                /*if (!matched) {
                    messages = await fieldValidation(messages, data, undefined, rule.validation, rule.message, rule.type, rule.priority);
                }*/
            } else if (Array.isArray(rule.field)) {
                for (const field of rule.field) {
                    messages = await fieldValidation(messages, data, field, rule.validation, rule.message, rule.type, rule.priority);
                }
            } else if (typeof rule.field === "string") {
                messages = await fieldValidation(messages, data, rule.field, rule.validation, rule.message, rule.type, rule.priority);
            }
        } else {
            messages = await formValidation(messages, data, rule.validation, rule.message, rule.type, rule.priority);
        }
    }
    return messages;
}